import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { observer } from "mobx-react";
import { RootStoreContext } from "../../mobX/RootStore";
// nodejs library that concatenates classes
import classNames from "classnames";
// @material-ui/icons
import LocationOn from "@material-ui/icons/LocationOn";
import EuroSymbolIcon from "@material-ui/icons/EuroSymbol";
import GroupIcon from "@material-ui/icons/Group";
import TodayIcon from "@material-ui/icons/Today";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import PhoneIcon from "@material-ui/icons/Phone";
// @material-ui/core customComponents
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
// core components
import Footer from "components/Footer/Footer";
import Parallax from "components/Parallax/Parallax";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
// custom components
import SportappHeader from "customComponents/SportappHeader/Header";
import ClubLogoImg from "customComponents/ClubLogoImg/ClubLogoImg";

import backgroundImage from "customAssets/img/ssb_background.png";
import style from "./styles";
import {
	displayTargetGroup,
	getCategoryImageURI,
	getSafeFullName,
	mapAgeGroupEnums,
	mapBillingIntervalEnums,
	mapDayEnumsToFullName,
} from "../../utils/Utils";
import Button from "../../components/CustomButtons/Button";

// eslint-disable-next-line react/prop-types
const MailTo = ({ email, subject, body, children }) => {
	return (
		<a
			href={`mailto:${email}?subject=${
				encodeURIComponent(subject) || ""
			}&body=${encodeURIComponent(body) || ""}`}
		>
			{children}
		</a>
	);
};

// eslint-disable-next-line react/prop-types
const PhoneCall = ({ number, children }) => {
	return <a href={`tel:${number}`}>{children}</a>;
};

// eslint-disable-next-line react/prop-types
const ActionChip = ({ children }) => {
	return (
		<Button
			round
			style={{
				backgroundColor: "#EEEEEE",
				paddingTop: 5,
				paddingLeft: 9,
				paddingBottom: 5,
				paddingRight: 14,
				margin: 4,
			}}
		>
			{children}
		</Button>
	);
};

const OfferingDetails = observer(() => {
	const store = useContext(RootStoreContext);
	const offeringStore = store.offeringStore;

	const offeringId = window.location.href.split("?id=").at(-1);
	const [offering, setOffering] = useState();
	const [backgroundImageURI, setBackgroundImageURI] = useState(backgroundImage);

	useEffect(() => {
		offeringStore.fetchOffering(offeringId);
	}, []);

	useEffect(() => {
		if (!offeringStore.isLoading) {
			const offering = offeringStore.getOfferingById(offeringId);
			setOffering(offering);
			if (offering.image) {
				setBackgroundImageURI(offering.image);
			} else {
				const categoryImageURI = getCategoryImageURI(offering.category);
				setBackgroundImageURI(categoryImageURI);
			}
		}
	}, [offeringStore.isLoading]);

	const classes = style();
	const imageClasses = classNames(
		classes.imgRaised,
		classes.imgRoundedCircle,
		classes.imgFluid,
	);
	return (
		<div>
			{offering?.title && offering?.clubName && (
				<Helmet>
					<title>
						{offering.title} - {offering.clubName} - Solingen Sportapp
					</title>
				</Helmet>
			)}
			<SportappHeader />
			<Parallax
				image={backgroundImageURI}
				filter="dark"
				className={classes.parallax}
			/>
			{offering && (
				<div className={classNames(classes.main, classes.mainRaised)}>
					<div className={classes.container}>
						<GridContainer justifyContent="center">
							<GridItem xs={12} sm={12} md={12}>
								<div className={classes.profile}>
									<div className={classes.imgWrapper}>
										<ClubLogoImg
											clubLogo={offering.clubLogo}
											alt={offering.title}
											imgClasses={imageClasses}
										/>
									</div>
									<div className={classes.name}>
										<h3 className={classes.title} style={{ marginBottom: 0 }}>
											{offering.title}
										</h3>
										<h5>{offering.clubName}</h5>
									</div>
									<div
										dangerouslySetInnerHTML={{
											__html: offering.descriptionAsHtml,
										}}
									/>
									<p className={classes.description}>
										{offering.targetGroup
											? displayTargetGroup(offering.targetGroup)
											: ""}
									</p>
									<GridContainer>
										{offering.schedules && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<TodayIcon className={classes.subHeaderIcon} />
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	Termine
																</h4>
																{offering.schedules.map((schedule, index) => {
																	return (
																		<div
																			key={index}
																			className={classes.listItem}
																		>
																			<p className={classes.listItemLeft}>
																				{mapDayEnumsToFullName(schedule.day)}:
																			</p>
																			<p>
																				{schedule.from} - {schedule.to}
																			</p>
																		</div>
																	);
																})}
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{offering.costs && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<EuroSymbolIcon
																	className={classes.subHeaderIcon}
																/>
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	Kosten
																</h4>
																{offering.costs.map((singleCost, index) => {
																	if (
																		!("ageGroup" in singleCost) &&
																		!("cost" in singleCost) &&
																		!("billingInterval" in singleCost)
																	)
																		return null;
																	const { ageGroup, cost, billingInterval } =
																		singleCost;
																	const costWithoutEuro = cost.replace("€", "");
																	return (
																		<div
																			key={index}
																			className={classes.listItem}
																		>
																			{ageGroup && (
																				<p className={classes.listItemLeft}>
																					{mapAgeGroupEnums(ageGroup)}:
																				</p>
																			)}
																			{costWithoutEuro.match(/^[., 0-9]+$/) ? (
																				costWithoutEuro !== "0" ? (
																					<p>
																						{cost} € im{" "}
																						{mapBillingIntervalEnums(
																							billingInterval,
																						)}
																					</p>
																				) : (
																					<p>kostenlos</p>
																				)
																			) : (
																				<p>{cost}</p>
																			)}
																		</div>
																	);
																})}
																<p>{offering.hints}</p>
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{offering.contact && (
											<GridItem sm={12} md={6}>
												<GridContainer>
													<GridItem sm={1} md={1} />
													<GridItem sm={11} md={11}>
														<GridContainer style={{ textAlign: "left" }}>
															<GridItem xs={1}>
																<GroupIcon className={classes.subHeaderIcon} />
															</GridItem>
															<GridItem
																xs={10}
																className={classes.subSectionContent}
															>
																<h4
																	className={classNames(
																		classes.left,
																		classes.title,
																	)}
																>
																	{getSafeFullName(offering.contact)}
																</h4>
																{offering.contact.email &&
																	offering.contact.email != "" && (
																		<p>E-Mail: {offering.contact.email}</p>
																	)}
																{offering.contact.phone &&
																	offering.contact.phone != "" && (
																		<p>Telefon: {offering.contact.phone}</p>
																	)}
																<div className={classes.actionChipsWrapper}>
																	<MailTo
																		email={offering.contact.email}
																		subject={offering.title}
																		body={""}
																	>
																		<ActionChip>
																			<div
																				className={classes.actionChipContent}
																			>
																				<MailOutlineIcon />
																				<p>Anschreiben</p>
																			</div>
																		</ActionChip>
																	</MailTo>
																	{offering.contact.phone &&
																		offering.contact.phone !== "" && (
																			<PhoneCall
																				number={offering.contact.phone}
																			>
																				<ActionChip>
																					<div
																						className={
																							classes.actionChipContent
																						}
																					>
																						<PhoneIcon />
																						<p>Anrufen</p>
																					</div>
																				</ActionChip>
																			</PhoneCall>
																		)}
																</div>
															</GridItem>
														</GridContainer>
													</GridItem>
												</GridContainer>
											</GridItem>
										)}
										{offering.location &&
											offering.location.street &&
											offering.location.houseNumber &&
											offering.location.zipCode &&
											offering.location.city && (
												<GridItem sm={12} md={6}>
													<GridContainer>
														<GridItem sm={1} md={1} />
														<GridItem sm={11} md={11}>
															<GridContainer style={{ textAlign: "left" }}>
																<GridItem xs={1}>
																	<LocationOn
																		className={classes.subHeaderIcon}
																	/>
																</GridItem>
																<GridItem
																	xs={10}
																	className={classes.subSectionContent}
																>
																	<h4
																		className={classNames(
																			classes.left,
																			classes.title,
																		)}
																	>
																		Adresse: {offering.location.name}
																	</h4>
																	<p>
																		{offering.location.street}{" "}
																		{offering.location.houseNumber}
																	</p>
																	<p>
																		{offering.location.zipCode}{" "}
																		{offering.location.city}
																	</p>
																</GridItem>
															</GridContainer>
														</GridItem>
													</GridContainer>
												</GridItem>
											)}
									</GridContainer>
								</div>
							</GridItem>
						</GridContainer>
						<div style={{ height: 20 }} />
					</div>
				</div>
			)}
			<Footer
				content={
					<div>
						<div className={classes.left}>
							<List className={classes.list}>
								<ListItem className={classes.inlineBlock}>
									<a href="/impressum/" className={classes.block}>
										Impressum
									</a>
								</ListItem>
								<ListItem className={classes.inlineBlock}>
									<a href="/datenschutz/" className={classes.block}>
										Datenschutzerklärung
									</a>
								</ListItem>
							</List>
						</div>
						<div className={classes.right}>
							&copy; {1900 + new Date().getYear()} Solinger Sportbund e.V.
						</div>
					</div>
				}
			/>
		</div>
	);
});

export default OfferingDetails;
